import * as React from "react";
import { Spinner } from "../../../components/ui/feedback";
import { CheckIcon, ChevronDownIcon, CircleAlertIcon, ClockIcon } from "../../../components/icons";
import { cn } from "../../../lib/utils";
import { StatusStep } from "../types/status-step";
import { liveSummary, settledSummary } from "../utils/workSummary";

export interface WorkingCardProps {
  steps: StatusStep[];
  /** The turn is still going: "Working", the running step pulsing, the list open. */
  live: boolean;
  /** How long the finished turn took, for the header. */
  durationMs?: number;
}

/**
 * A turn's working, as the web client's Worked card draws it: a 40px header -- a clock
 * in a tinted disc, "Working" or "Worked", a few words on what happened, a chevron --
 * that opens to one row per step.
 *
 * The same card in both states, in the same place, so a reply landing does not swap one
 * shape for another: live, it is open and names the step running; finished, it closes
 * to its header and sums the turn up ("3 steps · 12s"), and opens again on a click.
 *
 * It holds process only. The reply, its edits and its sources render outside it, so
 * collapsing it can never hide something with a consequence.
 */
const WorkingCard = ({ steps, live, durationMs }: WorkingCardProps) => {
  const [open, setOpen] = React.useState(live);
  const expanded = live || open;
  const summary = live ? liveSummary(steps) : settledSummary(steps, durationMs);
  const toggleable = !live && steps.length > 0;
  const bodyId = React.useId();

  return (
    <div
      data-testid="working-card"
      className="overflow-hidden rounded-lg border border-border bg-card"
    >
      <button
        type="button"
        disabled={!toggleable}
        aria-expanded={toggleable ? expanded : undefined}
        aria-controls={toggleable ? bodyId : undefined}
        onClick={() => setOpen((previous) => !previous)}
        className={cn(
          "flex h-10 w-full items-center gap-2.5 px-3 text-left outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40 focus-visible:ring-inset disabled:cursor-default",
          toggleable && "hover:bg-grey-50",
          expanded && "bg-grey-50"
        )}
      >
        <span className="flex size-5 flex-none items-center justify-center rounded-full bg-primary-tint text-primary-dark dark:text-primary-light">
          <ClockIcon className="size-3" strokeWidth={2.2} />
        </span>
        <span className="flex-none text-[13px] font-semibold text-strong">
          {live ? "Working" : "Worked"}
        </span>
        <span
          // Live, the summary is the running step: announced as it changes, and pulsing
          // so a long step reads as alive rather than stuck.
          aria-live={live ? "polite" : undefined}
          className={cn(
            "min-w-0 flex-1 truncate text-[12px] text-muted-foreground",
            live && "working-pulse"
          )}
        >
          {summary}
        </span>
        {toggleable && (
          <ChevronDownIcon
            className={cn(
              "size-3.5 flex-none text-muted-foreground transition-transform duration-150",
              expanded && "rotate-180"
            )}
            strokeWidth={2.2}
          />
        )}
      </button>

      {expanded && steps.length > 0 && (
        <div id={bodyId} className="flex flex-col gap-3 border-t border-border px-3.5 pt-3 pb-3.5">
          {steps.map((step, index) => (
            <StepRow key={index} step={step} />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * One step: a 20px disc saying how it went, its name, and -- when it failed -- why,
 * in words, beside it rather than behind a hover. A red mark with nothing next to it
 * cannot be acted on.
 */
const StepRow = ({ step }: { step: StatusStep }) => {
  const failed = step.status === "failed";
  const active = step.status === "active";
  return (
    <div className="grid grid-cols-[20px_minmax(0,1fr)] items-start gap-2.5">
      <span
        className={cn(
          "mt-px flex size-5 items-center justify-center rounded-full",
          failed ? "bg-error-tint text-error" : "bg-grey-100 text-muted-foreground"
        )}
      >
        {failed ? (
          <CircleAlertIcon className="size-3" strokeWidth={2.2} />
        ) : active ? (
          <Spinner size={11} className="text-muted-foreground" />
        ) : (
          <CheckIcon className="size-3" strokeWidth={2.4} />
        )}
      </span>
      <div className="flex min-w-0 flex-col gap-0.5">
        <span
          className={cn(
            "text-[13px] leading-[1.4] font-semibold",
            failed ? "text-error" : "text-strong",
            active && "working-pulse"
          )}
        >
          {step.label}
        </span>
        {failed && step.error && (
          <span className="text-[12.5px] leading-[1.55] [overflow-wrap:anywhere] text-error">
            {step.error}
          </span>
        )}
      </div>
    </div>
  );
};

export default WorkingCard;
